import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Translation {
  [key: string]: string;
}

interface TranslationContextType {
  translations: Translation;
  t: (key: string) => string;
  updateTranslation: (key: string, value: string) => void;
  language: string;
  setLanguage: (lang: string) => void;
}

const greekTranslations: Translation = {
  // Authentication
  'login': 'Σύνδεση',
  'email': 'Email',
  'password': 'Κωδικός Πρόσβασης',
  'login.button': 'Σύνδεση',
  'logout': 'Αποσύνδεση',
  
  // Navigation
  'dashboard': 'Πίνακας Ελέγχου',
  'customers': 'Πελάτες',
  'appointments': 'Ραντεβού',
  'reports': 'Αναφορές',
  'profile': 'Προφίλ',
  'translation.editor': 'Επεξεργαστής Μεταφράσεων',
  'translation.key': 'Κλειδί',
  'translation.value': 'Τιμή',
  'language': 'Γλώσσα',
  
  // Common
  'save': 'Αποθήκευση',
  'cancel': 'Ακύρωση',
  'edit': 'Επεξεργασία',
  'delete': 'Διαγραφή',
  'add': 'Προσθήκη',
  'update': 'Ενημέρωση',
  'search': 'Αναζήτηση',
  'name': 'Όνομα',
  'phone': 'Τηλέφωνο',
  'actions': 'Ενέργειες',
  'date': 'Ημερομηνία',
  'time': 'Ώρα',
  'status': 'Κατάσταση',
  'notes': 'Σημειώσεις',
  
  // Customer Management
  'customer.add': 'Προσθήκη Πελάτη',
  'customer.edit': 'Επεξεργασία Πελάτη',
  'customer.firstName': 'Όνομα',
  'customer.lastName': 'Επώνυμο',
  'customer.email': 'Email',
  'customer.phone': 'Τηλέφωνο',
  'customer.address': 'Διεύθυνση',
  'customer.preferences': 'Προτιμήσεις',
  'customer.businessName': 'Επωνυμία Επιχείρησης',
  'customer.contactPerson': 'Άτομο Επικοινωνίας',
  'customer.customerType': 'Τύπος Πελάτη',
  'selectCustomer': 'Επιλέξτε Πελάτη',
  
  // Reports
  'report': 'Αναφορά',
  'addReport': 'Προσθήκη Αναφοράς',
  'editReport': 'Επεξεργασία Αναφοράς',
  'createNewReport': 'Δημιουργία νέας αναφοράς',
  'updateReportDetails': 'Ενημέρωση στοιχείων αναφοράς',
  'businessName': 'Επωνυμία Επιχείρησης',
  'director': 'Διευθυντής',
  'product': 'Προϊόν',
  'competitor': 'Ανταγωνιστής',
  'goal': 'Στόχος',
  
  // Profile
  'profile.edit': 'Επεξεργασία Προφίλ',
  'profile.picture': 'Φωτογραφία Προφίλ',
  'profile.firstName': 'Όνομα',
  'profile.email': 'Email',
  'profile.password': 'Κωδικός Πρόσβασης',
  'profile.newPassword': 'Νέος Κωδικός',
  'profile.confirmPassword': 'Επιβεβαίωση Κωδικού',
  
  // Messages
  'welcome': 'Καλώς ήρθατε',
  'loading': 'Φόρτωση...',
  'error': 'Σφάλμα',
  'success': 'Επιτυχία',
  'noData': 'Δεν υπάρχουν δεδομένα'
};

const englishTranslations: Translation = {
  // Authentication
  'login': 'Login',
  'email': 'Email',
  'password': 'Password',
  'login.button': 'Sign In',
  'logout': 'Logout',
  
  // Navigation
  'dashboard': 'Dashboard',
  'customers': 'Customers',
  'appointments': 'Appointments',
  'reports': 'Reports',
  'profile': 'Profile',
  'translation.editor': 'Translation Editor',
  'translation.key': 'Key',
  'translation.value': 'Value',
  'language': 'Language',
  
  // Common
  'save': 'Save',
  'cancel': 'Cancel',
  'edit': 'Edit',
  'delete': 'Delete',
  'add': 'Add',
  'update': 'Update',
  'search': 'Search',
  'name': 'Name',
  'phone': 'Phone',
  'actions': 'Actions',
  'date': 'Date',
  'time': 'Time',
  'status': 'Status',
  'notes': 'Notes',
  
  // Customer Management
  'customer.add': 'Add Customer',
  'customer.edit': 'Edit Customer',
  'customer.firstName': 'First Name',
  'customer.lastName': 'Last Name',
  'customer.email': 'Email',
  'customer.phone': 'Phone',
  'customer.address': 'Address',
  'customer.preferences': 'Preferences',
  'customer.businessName': 'Business Name',
  'customer.contactPerson': 'Contact Person',
  'customer.customerType': 'Customer Type',
  'selectCustomer': 'Select Customer',
  
  // Reports
  'report': 'Report',
  'addReport': 'Add Report',
  'editReport': 'Edit Report',
  'createNewReport': 'Create a new report',
  'updateReportDetails': 'Update report details',
  'businessName': 'Business Name',
  'director': 'Director',
  'product': 'Product',
  'competitor': 'Competitor',
  'goal': 'Goal',
  
  // Profile
  'profile.edit': 'Edit Profile',
  'profile.picture': 'Profile Picture',
  'profile.firstName': 'First Name',
  'profile.email': 'Email',
  'profile.password': 'Password',
  'profile.newPassword': 'New Password',
  'profile.confirmPassword': 'Confirm Password',
  
  // Messages
  'welcome': 'Welcome',
  'loading': 'Loading...',
  'error': 'Error',
  'success': 'Success',
  'noData': 'No data available'
};

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customTranslations, setCustomTranslations] = useState<Translation>({});
  const [language, setLanguage] = useState('el');

  const getBaseTranslations = () => {
    return language === 'el' ? greekTranslations : englishTranslations;
  };

  const t = (key: string): string => {
    const baseTranslations = getBaseTranslations();
    return customTranslations[key] || baseTranslations[key] || key;
  };

  const updateTranslation = (key: string, value: string) => {
    setCustomTranslations(prev => ({ ...prev, [key]: value }));
  };

  const translations = { ...getBaseTranslations(), ...customTranslations };

  return (
    <TranslationContext.Provider value={{ translations, t, updateTranslation, language, setLanguage }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};